import React, { useEffect, useState } from 'react'
import { useSpotify } from '../hooks/useSpotify'
import { useNavigate } from 'react-router-dom'
import { FiCheck, FiAlertTriangle } from 'react-icons/fi'

const CallbackHandler: React.FC = () => {
  const { handleSpotifyCallback } = useSpotify()
  const [callbackStatus, setCallbackStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const processCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search)
        const code = params.get('code')
        const state = params.get('state')
        const storedState = localStorage.getItem('spotify_auth_state')
        
        if (!code) {
          const error = params.get('error') || 'No authorization code returned'
          console.error('Auth error:', error)
          setCallbackStatus('error')
          setErrorMessage(error)
          return
        }
        
        if (state !== storedState) {
          console.error('State mismatch. Possible CSRF attack.')
          setCallbackStatus('error')
          setErrorMessage('State verification failed. Please try again.')
          return
        }
        
        // Process the authorization code
        await handleSpotifyCallback(code)
        
        // Successfully authenticated
        setCallbackStatus('success')
        
        // Send message to parent window if this is opened as a popup
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ 
            type: 'spotify_auth_complete',
            success: true 
          }, window.location.origin)
          
          // Close this window/tab after a short delay
          setTimeout(() => {
            window.close()
          }, 1500)
        } else {
          // If not opened as popup, redirect back to home after 2 seconds
          setTimeout(() => {
            navigate('/')
          }, 2000)
        }
      } catch (error) {
        console.error('Error processing callback:', error)
        setCallbackStatus('error')
        setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred')
      }
    }
    
    processCallback()
  }, [handleSpotifyCallback, navigate])

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-spotify-dark text-white p-4">
      <div className="bg-spotify-light p-6 rounded-lg shadow-xl max-w-md w-full">
        {callbackStatus === 'processing' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-spotify-green mx-auto mb-4"></div>
            <h2 className="text-xl font-bold mb-2">Connecting to Spotify</h2>
            <p className="text-spotify-text-gray">Please wait while we complete the authentication...</p>
          </div>
        )}
        
        {callbackStatus === 'success' && (
          <div className="text-center">
            <div className="mx-auto mb-4 text-spotify-green">
              <FiCheck className="h-12 w-12 mx-auto" />
            </div>
            <h2 className="text-xl font-bold mb-2">Successfully Connected!</h2>
            <p className="text-spotify-text-gray">
              {window.opener ? 
                "This window will close automatically..." : 
                "Redirecting you back to the app..."}
            </p>
          </div>
        )}
        
        {callbackStatus === 'error' && (
          <div className="text-center">
            <div className="mx-auto mb-4 text-spotify-error">
              <FiAlertTriangle className="h-12 w-12 mx-auto" />
            </div>
            <h2 className="text-xl font-bold mb-2">Authentication Failed</h2>
            {errorMessage && (
              <p className="text-spotify-error mb-4">{errorMessage}</p>
            )}
            <button 
              onClick={() => navigate('/')}
              className="bg-spotify-green hover:bg-opacity-90 text-white font-bold py-2 px-4 rounded"
            >
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default CallbackHandler 